import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import countries from './countries.geo.json';

/**
 * AnimatedGlobe — premium white/gold fintech corporate globe.
 *
 * Transparent sphere + gold dot-matrix continents + animated gold arcs.
 * Fully unlit (MeshBasicMaterial) so the sphere never shades dark.
 */

const GOLD = '#C9A227';
const GOLD_LIGHT = '#F5D78A';

const offices = [
  { name: 'Fort Lauderdale', lat: 26.12, lng: -80.13 },
  { name: 'Nassau', lat: 25.05, lng: -77.35 },
  { name: 'London', lat: 51.51, lng: -0.13 },
  { name: 'New York', lat: 40.71, lng: -74.0 },
  { name: 'Toronto', lat: 43.65, lng: -79.38 },
  { name: 'Kingston', lat: 18.0, lng: -76.8 },
];

const arcs = [
  { start: 0, end: 1 },
  { start: 0, end: 2 },
  { start: 1, end: 2 },
  { start: 0, end: 3 },
  { start: 3, end: 4 },
  { start: 1, end: 5 },
  { start: 3, end: 2 },
].map(({ start, end }) => ({
  startLat: offices[start].lat,
  startLng: offices[start].lng,
  endLat: offices[end].lat,
  endLng: offices[end].lng,
  color: [GOLD, GOLD_LIGHT],
}));

const points = offices.map((o) => ({
  lat: o.lat,
  lng: o.lng,
  size: 0.6,
  color: GOLD,
  label: o.name,
}));

const rings = offices.map((o) => ({
  lat: o.lat,
  lng: o.lng,
  maxR: 4,
  propagationSpeed: 2,
  repeatPeriod: 1600,
}));

const AnimatedGlobe = ({ size = 480, className = '' }) => {
  const globeEl = useRef(null);
  const [GlobeComp, setGlobeComp] = useState(null);

  // Unlit, mostly-transparent white sphere — never shades dark.
  const globeMaterial = useMemo(
    () =>
      new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0.04,
        depthWrite: false,
      }),
    []
  );

  useEffect(() => {
    import('react-globe.gl').then((mod) => setGlobeComp(() => mod.default));
  }, []);

  useEffect(() => {
    if (!GlobeComp) return;
    let cancelled = false;
    let attempts = 0;

    const setup = () => {
      if (cancelled) return;
      const g = globeEl.current;
      if (!g || !g.scene) {
        if (attempts++ < 60) setTimeout(setup, 80);
        return;
      }
      try {
        // Remove all directional lighting so nothing renders dark.
        const scene = g.scene();
        scene.traverse((obj) => {
          if (obj.isDirectionalLight || obj.isHemisphereLight) obj.intensity = 0;
          if (obj.isAmbientLight) {
            obj.intensity = 1;
            obj.color?.set('#ffffff');
          }
        });
      } catch (e) { /* noop */ }

      try {
        const controls = g.controls();
        controls.autoRotate = true;
        controls.autoRotateSpeed = 0.45;
        controls.enableZoom = false;
        controls.enablePan = false;
        g.pointOfView({ lat: 25, lng: -50, altitude: 2.4 }, 0);
      } catch (e) { /* noop */ }
    };
    setup();
    return () => { cancelled = true; };
  }, [GlobeComp]);

  if (!GlobeComp) {
    return (
      <div
        className={`relative inline-block ${className}`}
        style={{ width: size, height: size }}
        aria-hidden="true"
      >
        <div className="absolute inset-0 rounded-full bg-[#C9A227]/5 animate-pulse" />
      </div>
    );
  }

  return (
    <div
      className={`relative inline-block ${className}`}
      style={{ width: size, height: size }}
      data-testid="animated-globe"
      aria-hidden="true"
    >
      {/* Soft gold backdrop glow */}
      <div
        className="absolute inset-0 rounded-full pointer-events-none"
        style={{
          background:
            'radial-gradient(circle, rgba(201,162,39,0.16) 0%, rgba(201,162,39,0.04) 55%, transparent 75%)',
        }}
      />

      <GlobeComp
        ref={globeEl}
        width={size}
        height={size}
        backgroundColor="rgba(0,0,0,0)"
        // Custom unlit material → sphere is transparent white, never dark.
        globeMaterial={globeMaterial}
        showAtmosphere={true}
        atmosphereColor={GOLD}
        atmosphereAltitude={0.16}
        // Dot-matrix continents (fintech aesthetic)
        hexPolygonsData={countries.features}
        hexPolygonResolution={3}
        hexPolygonMargin={0.5}
        hexPolygonUseDots={true}
        hexPolygonColor={() => GOLD}
        hexPolygonAltitude={0.005}
        // Animated gold arcs
        arcsData={arcs}
        arcColor="color"
        arcDashLength={0.4}
        arcDashGap={0.2}
        arcDashAnimateTime={3000}
        arcStroke={0.5}
        arcAltitudeAutoScale={0.4}
        // Office points
        pointsData={points}
        pointAltitude={0.02}
        pointRadius="size"
        pointColor="color"
        // Pulse rings
        ringsData={rings}
        ringColor={() => GOLD}
        ringMaxRadius="maxR"
        ringPropagationSpeed="propagationSpeed"
        ringRepeatPeriod="repeatPeriod"
      />
    </div>
  );
};

export default AnimatedGlobe;
